import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  formatDetailedTimestamp,
  formatRelativeTime,
} from '../utils/fliqueDates';
import { userTabInitials } from '../utils/userInitials';
import ScoreMeter from '../components/ScoreMeter';
import MovieDetailModal from '../components/MovieDetailModal';
import { useToast } from '../context/ToastContext';
import {
  buildScoreMap,
  compositeSortCompare,
  findExistingRankedPost,
  findViewerPostForMovie,
  findWatchlistEntryForMovie,
  isRankedPost,
} from '../utils/rankingBins';

export default function ProfilePage({
  user,
  posts,
  list,
  viewerPosts: viewerPostsProp,
  viewerList: viewerListProp,
  onRemoveMovie,
  onRemoveWatchlistEntry,
  onRankAgain,
  onBeginRanking,
  onAddToWatchMovieList,
  isSelf = true,
  relationshipNode = null,
}) {
  const { showToast } = useToast();
  const location = useLocation();
  const viewerPosts = viewerPostsProp ?? posts;
  const viewerList = viewerListProp ?? list;
  const [activeTab, setActiveTab] = useState('favorites'); // recents | favorites | want-to-watch
  const [tabIndicatorStyle, setTabIndicatorStyle] = useState(null);
  const tabRefs = useRef({});
  const [detailModalMovie, setDetailModalMovie] = useState(null);
  const [detailModalInitialStep, setDetailModalInitialStep] = useState('detail');
  const [pendingDeleteMovie, setPendingDeleteMovie] = useState(null);
  /** When set, completing a rank from the modal removes this watchlist row (opened from watchlist). */
  const [detailModalRemoveWatchlistEntryId, setDetailModalRemoveWatchlistEntryId] =
    useState(null);
  const [pendingDeleteWatchlist, setPendingDeleteWatchlist] = useState(null);
  const handle =
    user?.username && String(user.username).trim()
      ? String(user.username).trim()
      : 'username';
  const firstname =
    user?.firstname && String(user.firstname).trim()
      ? String(user.firstname).trim()
      : '';
  const lastname =
    user?.lastname && String(user.lastname).trim()
      ? String(user.lastname).trim()
      : '';

  // Keep the avatar label consistent with the feed: for other users, prefer
  // avatar_initials → username, not firstname/lastname-derived initials.
  const headerInitials = useMemo(() => {
    if (isSelf) return userTabInitials(user);
    return userTabInitials({ ...(user || {}), firstname: '', lastname: '' });
  }, [user, isSelf]);

  const scoreMap = useMemo(() => buildScoreMap(posts), [posts]);
  const detailModalExistingPost = useMemo(
    () =>
      detailModalMovie
        ? findExistingRankedPost(viewerPosts, detailModalMovie)
        : null,
    [viewerPosts, detailModalMovie],
  );
  const detailModalViewerHasWatched = useMemo(
    () =>
      Boolean(
        detailModalMovie && findViewerPostForMovie(viewerPosts, detailModalMovie),
      ),
    [viewerPosts, detailModalMovie],
  );
  const detailModalWatchlistEntry = useMemo(
    () =>
      detailModalMovie
        ? findWatchlistEntryForMovie(viewerList, detailModalMovie)
        : null,
    [viewerList, detailModalMovie],
  );
  const detailModalInWatchlist = Boolean(detailModalWatchlistEntry);
  const detailModalWatchlistEntryId = detailModalWatchlistEntry?.id;

  const openMovieDetailModal = (movie, { removeWatchlistEntryId = null } = {}) => {
    if (!movie?.title) return;
    setDetailModalInitialStep('detail');
    setDetailModalRemoveWatchlistEntryId(removeWatchlistEntryId);
    setDetailModalMovie(movie);
  };

  const closeMovieDetailModal = () => {
    setDetailModalMovie(null);
    setDetailModalInitialStep('detail');
    setDetailModalRemoveWatchlistEntryId(null);
  };

  const requestDeleteMovie = (post) => {
    if (!post?.id) return;
    setPendingDeleteMovie({
      id: post.id,
      title: post.movie?.title || 'Untitled',
      year: post.movie?.year || '',
    });
  };

  const cancelDeleteMovie = () => {
    setPendingDeleteMovie(null);
  };

  const confirmDeleteMovie = async () => {
    if (!pendingDeleteMovie?.id || !onRemoveMovie) return;
    try {
      await onRemoveMovie(pendingDeleteMovie.id);
      showToast('Removed logged movie');
    } finally {
      setPendingDeleteMovie(null);
    }
  };

  const requestDeleteWatchlistEntry = (entry) => {
    if (!entry?.id) return;
    setPendingDeleteWatchlist({
      id: entry.id,
      title: entry.movie?.title || 'Untitled',
      year: entry.movie?.year || '',
    });
  };

  const cancelDeleteWatchlistEntry = () => {
    setPendingDeleteWatchlist(null);
  };

  const confirmDeleteWatchlistEntry = async () => {
    if (!pendingDeleteWatchlist?.id || !onRemoveWatchlistEntry) return;
    try {
      await onRemoveWatchlistEntry(pendingDeleteWatchlist.id);
      showToast('Removed from watchlist');
    } finally {
      setPendingDeleteWatchlist(null);
    }
  };

  const displayPosts = useMemo(() => {

    if (activeTab === 'watchlist') {
      const arr = Array.isArray(list) ? [...list] : [];
      return arr;
    }

    const arr = Array.isArray(posts) ? [...posts] : [];
    if (activeTab === 'favorites') {
      arr.sort(compositeSortCompare);
      return arr;
    }
    arr.sort((a, b) => {
      const ac = a.createdAt ? Date.parse(a.createdAt) : 0;
      const bc = b.createdAt ? Date.parse(b.createdAt) : 0;
      if (ac !== bc) return bc - ac;
      const aw = a.watchedAt ? Date.parse(a.watchedAt) : 0;
      const bw = b.watchedAt ? Date.parse(b.watchedAt) : 0;
      return bw - aw;
    });
    return arr;
  }, [posts, list, activeTab]);

  useEffect(() => {
    const updateIndicator = () => {
      const activeEl = tabRefs.current[activeTab];
      if (!activeEl) return;
      setTabIndicatorStyle({
        width: `${activeEl.offsetWidth}px`,
        transform: `translateX(${activeEl.offsetLeft}px)`,
      });
    };

    updateIndicator();
    window.addEventListener('resize', updateIndicator);
    return () => window.removeEventListener('resize', updateIndicator);
  }, [activeTab]);

  return (
    <div className="flique-profile">
      <header className="flique-profile-header">
        <span
          className="flique-tab-icon flique-tab-icon-avatar flique-pfp--profile"
          aria-hidden="true"
        >
          {headerInitials}
        </span>
        <div className="flique-profile-identity">
          <div className="flique-profile-username">@{handle}</div>
          {(firstname || lastname) ? (
            <div className="flique-profile-subtitle text-muted">
              {[firstname, lastname].filter(Boolean).join(' ')}
            </div>
          ) : null}
        </div>
      </header>

      {relationshipNode}

      <div className="flique-profile-movies-block">
        <h2 className="flique-profile-movies-heading">
        </h2>
        <nav className="flique-profile-tabs" aria-label="Profile tabs">
          <button
            type="button"
            ref={(el) => {
              tabRefs.current.favorites = el;
            }}
            className={`flique-tablink ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            Favorites
          </button>
          <button
            type="button"
            ref={(el) => {
              tabRefs.current.recents = el;
            }}
            className={`flique-tablink ${activeTab === 'recents' ? 'active' : ''}`}
            onClick={() => setActiveTab('recents')}
          >
            Recents
          </button>
          <button
            type="button"
            ref={(el) => {
              tabRefs.current.watchlist = el;
            }}
            className={`flique-tablink ${activeTab === 'watchlist' ? 'active' : ''}`}
            onClick={() => setActiveTab('watchlist')}
          >
            Want to Watch
          </button>
          <span
            className="flique-profile-tabs-indicator"
            style={tabIndicatorStyle || undefined}
            aria-hidden="true"
          />
        </nav>
      </div>

      <section className="flique-profile-feed">
        {displayPosts && displayPosts.length > 0 ? (
          displayPosts.map((post) => {
            const scoreInfo = isRankedPost(post) ? scoreMap.get(post.id) : null;
            return (
              <MovieRow
                post={post}
                key={post.id}
                postId={post.id}
                title={post.movie?.title || 'Untitled'}
                year={post.movie?.year || ''}
                posterUrl={post.movie?.poster_url}
                loggedAt={post.createdAt}
                review={post.notes || ''}
                scoreInfo={scoreInfo}
                favoritesLayout={activeTab === 'favorites'}
                recentsLayout={activeTab === 'recents'}
                listLayout={activeTab === 'watchlist'}
                onRemoveMovie={onRemoveMovie}
                onRequestDeleteMovie={() => requestDeleteMovie(post)}
                onRemoveWatchlistEntry={onRemoveWatchlistEntry}
                onRequestDeleteWatchlistEntry={() =>
                  requestDeleteWatchlistEntry(post)
                }
                onRankAgain={onRankAgain}
                onOpenMovieDetail={() => {
                  if (!post.movie) return;
                  if (activeTab === 'watchlist' && isSelf) {
                    openMovieDetailModal(post.movie, {
                      removeWatchlistEntryId: post.id,
                    });
                  } else {
                    openMovieDetailModal(post.movie);
                  }
                }}
                onRankWatchlistEntry={(watchlistEntry) => {
                  if (!watchlistEntry?.movie) return;
                  openMovieDetailModal(watchlistEntry.movie, {
                    removeWatchlistEntryId: watchlistEntry.id,
                  });
                }}
                isSelf={isSelf}
              />
            );
          })
        ) : (
          <div className="text-muted small">
            {activeTab === 'favorites'
              ? isSelf
              ? 'No ranked favorites yet. Add and rank a movie first.'
              : 'No ranked favorites yet.'
            : activeTab === 'recents'
              ? isSelf
                ? 'No logged movies yet. Tap + to add your first film.'
                : 'No logged movies yet.'
              : isSelf
                ? 'No movies on your watchlist yet.'
                : 'No movies on this watchlist yet.'}
                    </div>
          )}
      </section>
      <MovieDetailModal
        movie={detailModalMovie}
        existingPost={detailModalExistingPost}
        open={detailModalMovie != null}
        initialStep={detailModalInitialStep}
        onClose={closeMovieDetailModal}
        onBeginRanking={(watchedMovie) => {
          if (!onBeginRanking) return;
          const removeWatchlistEntryId = detailModalRemoveWatchlistEntryId;
          onBeginRanking({
            ...watchedMovie,
            afterRankNavigate: location.pathname,
            ...(removeWatchlistEntryId != null
              ? { removeWatchlistEntryId }
              : {}),
            onBackToTierSelection: () => {
              setDetailModalMovie(watchedMovie?.selectedMovie || null);
              setDetailModalInitialStep('pickBin');
              setDetailModalRemoveWatchlistEntryId(removeWatchlistEntryId ?? null);
            },
          });
          closeMovieDetailModal();
        }}
        onAddToWatchMovieList={onAddToWatchMovieList ?? null}
        onRemoveFromWatchlist={onRemoveWatchlistEntry}
        viewerHasWatchedMovie={detailModalViewerHasWatched}
        inWatchlist={detailModalInWatchlist}
        watchlistEntryId={detailModalWatchlistEntryId}
      />
      {pendingDeleteMovie ? (
        <div className="flique-modal-backdrop">
          <div className="flique-modal card flique-card flique-friends-modal flique-remove-friend-modal">
            <div className="card-body text-center">
              <h2 className="h5 mb-2">Delete rating?</h2>
              <p className="small text-muted mb-3">
                Are you sure you want to delete your rating for{' '}
                {`${pendingDeleteMovie.title}${pendingDeleteMovie.year ? ` (${pendingDeleteMovie.year})` : ''}`}?
              </p>
              <div className="d-flex gap-2 justify-content-center flique-remove-friend-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light flique-remove-friend-action-btn"
                  onClick={cancelDeleteMovie}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-flique flique-remove-friend-action-btn"
                  onClick={confirmDeleteMovie}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
      {pendingDeleteWatchlist ? (
        <div className="flique-modal-backdrop">
          <div className="flique-modal card flique-card flique-friends-modal flique-remove-friend-modal">
            <div className="card-body text-center">
              <h2 className="h5 mb-2">Remove from watchlist?</h2>
              <p className="small text-muted mb-3">
                Are you sure you want to remove{' '}
                {`${pendingDeleteWatchlist.title}${pendingDeleteWatchlist.year ? ` (${pendingDeleteWatchlist.year})` : ''}`}{' '}
                from your watchlist?
              </p>
              <div className="d-flex gap-2 justify-content-center flique-remove-friend-actions">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-light flique-remove-friend-action-btn"
                  onClick={cancelDeleteWatchlistEntry}
                >
                  No
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-flique flique-remove-friend-action-btn"
                  onClick={confirmDeleteWatchlistEntry}
                >
                  Yes
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function MovieRow({
  post,
  postId,
  title,
  year,
  posterUrl,
  loggedAt,
  review,
  scoreInfo,
  favoritesLayout,
  recentsLayout,
  listLayout,
  onRemoveMovie,
  onRequestDeleteMovie,
  onRemoveWatchlistEntry,
  onRequestDeleteWatchlistEntry,
  onRankAgain,
  onRankWatchlistEntry,
  onOpenMovieDetail,
  isSelf,
}) {
  const titleLine =
    year !== '' && year != null ? `${title} (${year})` : title;
  const loggedLabel = loggedAt ? formatRelativeTime(loggedAt) : '';
  const loggedTooltip = loggedAt ? formatDetailedTimestamp(loggedAt) : '';

  const rowInteractive = typeof onOpenMovieDetail === 'function';

  return (
    <div
      className={`flique-movie-row${rowInteractive ? ' flique-movie-row--clickable' : ''}`}
    >
      <button
        type="button"
        className="flique-movie-row-hit flique-poster-cell"
        onClick={() => onOpenMovieDetail && onOpenMovieDetail()}
        disabled={!rowInteractive}
        aria-label={rowInteractive ? `Open details for ${titleLine}` : undefined}
      >
        {posterUrl ? (
          <img src={posterUrl} alt="" className="flique-poster-img" />
        ) : (
          <div className="flique-poster-placeholder">Poster</div>
        )}
      </button>

      <div className="flique-movie-meta">
        <div className="flique-movie-line1">
          {rowInteractive ? (
            <button
              type="button"
              className="flique-movie-title-btn"
              onClick={() => onOpenMovieDetail()}
            >
              <span className="flique-movie-title">{titleLine}</span>
            </button>
          ) : (
            <span className="flique-movie-title">{titleLine}</span>
          )}
        </div>
        
        {listLayout ? null : (
          <div className="flique-movie-date text-muted">
            {loggedLabel ? (
              <time
                dateTime={loggedAt}
                title={loggedTooltip}
                aria-label={loggedTooltip}
              >
                {loggedLabel}
              </time>
            ) : (
              'Date unknown'
            )}
          </div>
        )}
        
        {review ? (
          <div className="flique-movie-review">{review}</div>
        ) : null}
        <div className="flique-row-actions">
          {listLayout && onRankWatchlistEntry && isSelf ? (
            <button
              type="button"
              className="btn btn-sm flique-row-action-btn flique-row-action-btn--icon"
              onClick={(e) => {
                e.stopPropagation();
                onRankWatchlistEntry(post);
              }}
              aria-label="Rank movie"
              title="Rank movie"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3v18" />
                <path d="M3 12h18" />
              </svg>
              <span className="visually-hidden">Rank movie</span>
            </button>
          ) : null}
          {listLayout && onRemoveWatchlistEntry && isSelf ? (
            <button
              type="button"
              className="btn btn-sm flique-row-action-btn flique-row-action-btn--icon flique-row-action-btn--delete"
              onClick={(e) => {
                e.stopPropagation();
                if (onRequestDeleteWatchlistEntry) {
                  onRequestDeleteWatchlistEntry();
                  return;
                }
                onRemoveWatchlistEntry(postId);
              }}
              aria-label="Remove movie"
              title="Remove movie"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
                <path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              <span className="visually-hidden">Remove movie</span>
            </button>
          ) : null}
          {onRankAgain && scoreInfo ? (
            <button
              type="button"
              className="btn btn-sm flique-row-action-btn flique-row-action-btn--icon"
              onClick={(e) => {
                e.stopPropagation();
                onRankAgain(post);
              }}
              aria-label="Rank again"
              title="Rank again"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="23 4 23 10 17 10" />
                <polyline points="1 20 1 14 7 14" />
                <path d="M3.51 9a9 9 0 0 1 14.13-3.36L23 10" />
                <path d="M20.49 15a9 9 0 0 1-14.13 3.36L1 14" />
              </svg>
              <span className="visually-hidden">Rank again</span>
            </button>
          ) : null}
          {!listLayout && onRemoveMovie ? (
            <button
              type="button"
              className="btn btn-sm flique-row-action-btn flique-row-action-btn--icon flique-row-action-btn--delete"
              onClick={(e) => {
                e.stopPropagation();
                if (onRequestDeleteMovie) {
                  onRequestDeleteMovie();
                  return;
                }
                onRemoveMovie(postId);
              }}
              aria-label="Remove movie"
              title="Remove movie"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
                <path d="M19 6l-1 14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1L5 6" />
                <line x1="10" y1="11" x2="10" y2="17" />
                <line x1="14" y1="11" x2="14" y2="17" />
              </svg>
              <span className="visually-hidden">Remove movie</span>
            </button>
          ) : null}
        </div>
      </div>

      {scoreInfo ? (
        <ScoreMeter score={scoreInfo.score} kind={scoreInfo.kind} />
      ) : (
        <div className="flique-score-meter-placeholder" aria-hidden="true" />
      )}
    </div>
  );
}
